<template>
  <LandingLayout>
    <Head :title="title" />
    <!-- Main Header -->
    <Header :subscriptionCoursesCount="subscriptionCoursesCount" />
    <!-- Social Proof -->
    <div v-if="landingOptions.show_social_proof">
      <SocialProof />
    </div>
    <!-- Call to Action -->
    <main
      fluid
      class="_background:white"
      size="sm"
      data-aos="fade-in"
      data-aos-duration="1300"
      data-aos-once="true">
      <!-- Featured Courses Section using CourseCard component -->
      <section
        v-if="landingOptions.show_courses"
        class="_background:white _padding-top:4 _padding-bottom:2">
        <IContainer>
          <i-row center>
            <i-column>
              <h2 class="_color:primary _text:left _text:4xl _text:bold _margin:0">
                Featured Courses
              </h2>
              <p
                class="_text:left _text:lg _text:normal _text:dark-35 _margin-bottom:2 _margin-top:1/4">
                Practitioner-led frameworks on CSRD, LL97, green finance, supply chain risk, and AI
                workflows.
              </p>
            </i-column>
          </i-row>
          <!-- Course Cards using same layout as course listing -->
          <div class="_max-height:100%">
            <div v-if="featuredCourses.length >= 1">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                <CourseFeatured
                  v-for="course in featuredCourses"
                  :key="course.id"
                  :course="course"
                  :courseImageUrl="courseImageUrl"
                  @navigate="navigateToCourse" />
              </div>
            </div>

            <!-- No Featured Courses State -->
            <div v-else>
              <ICard color="light" class="_margin-bottom:2">
                <template #header>
                  <div class="h4 _text:dark-35 _text:center">No Featured Courses Available</div>
                </template>
                <div class="_padding:2">
                  <IListGroup :border="false">
                    <IListGroupItem>
                      <div class="_display:flex _justify-content:center">
                        <ExclamationCircleIcon class="_color:primary empty-icon" />
                      </div>
                      <p class="_padding-top:1 _text:lg _text:dark-35 _text:center">
                        Check back soon for featured courses from our climate experts.
                      </p>
                    </IListGroupItem>
                  </IListGroup>
                </div>
              </ICard>
            </div>
          </div>

          <i-row end>
            <i-column>
              <Link
                :href="route('page.courses')"
                class="button -outline -primary _text:white -lg _padding-x:2 _margin-top:0">
                View All Courses
              </Link>
            </i-column>
          </i-row>
        </IContainer>
      </section>

      <!-- Climate Specilaization Variation 2: Modern Pill Tags Layout HERE -->
      <section class="_background:white _padding-top:0 _padding-bottom:4">
        <IContainer>
          <IRow center>
            <IColumn>
              <div class="specializations-wrapper">
                <!-- Header -->
                <div class="_margin-bottom:2">
                  <h2 class="_color:primary _text:left _text:4xl _text:bold _margin:0">
                    Specializations in Compliance & AI
                  </h2>
                  <p
                    class="_text:left _text:lg _text:normal _text:dark-35 _margin-bottom:2 _margin-top:1/4">
                    Eight specializations across compliance, supply chain, energy policy, and AI.
                  </p>
                </div>

                <!-- Learning Paths -->
                <div class="learning-paths">
                  <!-- Supply Chain Risk - Priority -->
                  <div
                    class="path-item priority-path"
                    data-aos="fade-up"
                    data-aos-duration="400"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Supply Chain Risk Management</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Product-level mapping, UFLPA compliance, resilience strategies
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Green Finance -->
                  <div
                    class="path-item finance-path"
                    data-aos="fade-up"
                    data-aos-duration="500"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Green Finance & ESG</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Sustainable investing, risk assessment, impact measurement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- AI & Cleantech Innovation -->
                  <div
                    class="path-item cleantech-path"
                    data-aos="fade-up"
                    data-aos-duration="900"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">AI & Cleantech Innovation</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            AI grid integration & utility scaling, market entry, commercialization
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Energy Access Policy -->
                  <div
                    class="path-item access-path"
                    data-aos="fade-up"
                    data-aos-duration="600"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Energy Access Policy</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Policies for fair, affordable, and green energy access
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Renewable Energy -->
                  <div
                    class="path-item renewable-path"
                    data-aos="fade-up"
                    data-aos-duration="700"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Renewable Energy Systems</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Project development, technical strategies, market insights
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Compliance & Risk -->
                  <div
                    class="path-item compliance-path"
                    data-aos="fade-up"
                    data-aos-duration="800"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Regulatory Compliance</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            CSRD, LkSG, LL97, disclosure frameworks
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Climate Risk Assessment -->
                  <div
                    class="path-item risk-path"
                    data-aos="fade-up"
                    data-aos-duration="1000"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">Climate Risk Assessment</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Physical risk modeling, scenario planning, adaptation
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Future Specializations -->
                  <div
                    class="path-item future-path"
                    data-aos="fade-up"
                    data-aos-duration="1100"
                    data-aos-once="true">
                    <div class="path-content">
                      <div class="path-header">
                        <div class="path-info">
                          <h3 class="path-title _text:dark-50">More Specializations</h3>
                          <p class="path-description text-lg _text:normal _text:dark-35">
                            Additional learning paths launching throughout 2026
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </IColumn>
          </IRow>
        </IContainer>
      </section>

      <!-- Featured Certifications Section -->
      <section class="_background:white _padding-y:0">
        <IContainer>
          <i-row center>
            <i-column>
              <h2 class="_color:primary _text:left _text:4xl _text:bold _margin:0">
                Prove your expertise. Drive real impact.
              </h2>
              <p
                class="_text:left _text:lg _text:normal _text:dark-35 _margin-bottom:2 _margin-top:1/4">
                Credentials built on real compliance and energy work, recognized by employers and
                auditors alike.
              </p>
            </i-column>
          </i-row>

          <!-- Certification Cards using same layout as course listing -->
          <div class="_max-height:100%">
            <div v-if="featuredCertifications.length >= 1">
              <div class="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                <CertificationFeatured
                  v-for="certification in featuredCertifications"
                  :key="certification.id"
                  :certification="certification"
                  @navigate="navigateToCertification" />
              </div>
            </div>

            <!-- No Featured Certifications State -->
            <div v-else>
              <ICard color="light" class="_margin-bottom:2">
                <template #header>
                  <div class="h4 _text:dark-35 _text:center">
                    No Featured Certifications Available
                  </div>
                </template>
                <div class="_padding:2">
                  <IListGroup :border="false">
                    <IListGroupItem>
                      <div class="_display:flex _justify-content:center">
                        <ExclamationCircleIcon class="_color:primary empty-icon" />
                      </div>
                      <p class="_padding-top:1 _text:lg _text:dark-35 _text:center">
                        Check back soon for featured certifications from our climate experts.
                      </p>
                    </IListGroupItem>
                  </IListGroup>
                </div>
              </ICard>
            </div>
          </div>
        </IContainer>
      </section>

      <!-- Testimonials Section -->
      <Testimonials />

      <!-- Subscribe to demo -->
      <i-container>
        <div
          class="relative overflow-hidden rounded-md bg-[#09315f] border-2 border-[#009ddb] mb-8">
          <!-- SVG Grid Background -->
          <div class="absolute inset-0 w-full h-full opacity-50">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 600 300"
              preserveAspectRatio="none"
              class="w-full h-full">
              <!-- Diagonal lines -->
              <line
                x1="0"
                y1="0"
                x2="600"
                y2="300"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="100"
                y1="0"
                x2="600"
                y2="250"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="200"
                y1="0"
                x2="600"
                y2="200"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="300"
                y1="0"
                x2="600"
                y2="150"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="400"
                y1="0"
                x2="600"
                y2="100"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="500"
                y1="0"
                x2="600"
                y2="50"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />

              <!-- Horizontal lines -->
              <line
                x1="0"
                y1="50"
                x2="600"
                y2="50"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="0"
                y1="100"
                x2="600"
                y2="100"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="0"
                y1="150"
                x2="600"
                y2="150"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="0"
                y1="200"
                x2="600"
                y2="200"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
              <line
                x1="0"
                y1="250"
                x2="600"
                y2="250"
                stroke="rgba(255, 255, 255, 0.5)"
                stroke-width="1.5" />
            </svg>
          </div>

          <!-- Tint Overlay (Optional) -->
          <div class="absolute inset-0 w-full h-full bg-[#092e58] opacity-70"></div>

          <!-- Content -->
          <i-row center class="relative z-10">
            <div
              class="container flex flex-col flex-wrap content-center justify-center pt-8 md:pt-10 lg:pt-12">
              <h2
                class="text-gray-100 text-center text-3xl sm:text-4xl md:text-5xl mb-2 md:mb-3 px-5 md:px-4 font-medium leading-tight">
                Deploy practitioner-proven frameworks.
                <br class="hidden sm:block" />
                Eliminate the headache.
              </h2>
            </div>
            <i-column xs="12" lg="8" xl="8" class="pt-4 pb-20">
              <p class="text-lg font-normal text-gray-300 mb-2 px-4 md:px-0">
                Get the latest on energy policy, AI tools, courses, and supply chain intel.
              </p>
              <form @submit.prevent="subscribe" method="POST" id="bottom-cta-form">
                <div class="_xs:hidden _sm:visible px-8">
                  <IInput
                    class="_padding:1"
                    v-model="form.email"
                    for="email"
                    name="email"
                    id="email_lg"
                    type="email"
                    placeholder="Enter Email Address">
                    <template #append>
                      <IButton
                        id="email_btn_sm"
                        class="text-gray-100"
                        type="submit"
                        color="primary"
                        alt="Subscribe">
                        <ILoader v-if="loading"></ILoader>
                        <div v-else>
                          <span class="_xs:hidden _md:visible text-xl font-semibold text-gray-100">
                            Subscribe
                          </span>
                          <span class="_xs:visible _md:hidden text-xl font-semibold text-gray-100">
                            Subscribe
                          </span>
                        </div>
                      </IButton>
                    </template>
                  </IInput>
                </div>
                <div class="_xs:visible _sm:hidden px-8">
                  <IInput
                    class="_padding:1"
                    v-model="form.email"
                    for="email"
                    name="email"
                    id="email_sm"
                    type="email"
                    size="sm"
                    placeholder="Enter Email Address" />
                  <div class="_display:flex _justify-content:end">
                    <IButton
                      id="email_btn_lg"
                      class="mt-4"
                      type="submit"
                      color="primary"
                      size="lg"
                      alt="Subscribe">
                      <ILoader v-if="loading"></ILoader>
                      <div v-else>
                        <span class="_xs:hidden _md:visible text-xl font-semibold text-gray-100">
                          Subscribe
                        </span>
                        <span class="_xs:visible _md:hidden text-xl font-semibold text-gray-100">
                          Subscribe
                        </span>
                      </div>
                    </IButton>
                  </div>
                </div>
              </form>
              <div v-if="errors.email" class="text-white px-2 mt-2 md:px-0">
                <span>
                  <ExclamationTriangleIcon class="inline-flex text-red-500 email-error-icon" />
                  {{ errors.email }}
                </span>
              </div>
            </i-column>
          </i-row>
        </div>
      </i-container>
    </main>
  </LandingLayout>
</template>

<script setup>
  import { computed, ref, watchEffect } from 'vue'
  import { Head, Link, router, useForm } from '@inertiajs/vue3'
  import { reactiveComputed, useCounter, computedAsync } from '@vueuse/core'
  import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { useMeta } from '@/Composables/useMeta'
  import LandingLayout from '@/Layouts/LandingLayout.vue'
  import config from '@config'
  import CertificationFeatured from '@/Components/CertificationFeatured.vue'
  import CourseFeatured from '@/Components/CourseFeatured.vue'
  import Header from '@/Components/Landing/Header.vue'
  import SocialProof from '@/Components/Landing/SocialProof.vue'
  import Testimonials from '@/Components/Landing/Testimonials.vue'

  const props = defineProps({
    errors: Object,
    title: String,
    meta: Object,
    canLogin: Boolean,
    canRegister: Boolean,
    certifications: Object,
    courses: Object,
    courseImageUrl: String,
    levels: Array,
    types: Array,
    fetchAmount: Number,
    subscriptionCoursesCount: Number,
    title: {
      type: String,
      default: 'Climate64 Default',
    },
  })

  // landing config
  const landingOptions = config.landing

  // Safe route helper function
  const safeRoute = (routeName, fallbackUrl) => {
    return typeof route !== 'undefined' ? route(routeName) : fallbackUrl
  }

  // home props
  const courseData = props.courses
  const fetchAmount = props.fetchAmount
  const searchQuery = ref('')
  const levelQuery = ref('')
  const loading = ref(false)
  const toggle = ref(true)

  let courseList = ref('')
  const { count, inc } = useCounter(fetchAmount, { min: 0, max: courseData.total })

  // search data
  let filteredCourseData = reactiveComputed(() => {
    // filter the data and match case
    return courseData.data.filter((course) => {
      // return predetermined columns
      if (!searchQuery.value.length > 0 && !!levelQuery.value) {
        return _.toLower(course.level.label).indexOf(levelQuery.value.label.toLowerCase()) != -1
      } else {
        if (searchQuery || levelQuery === null) {
          return (
            _.toLower(course.name).indexOf(searchQuery.value.toLowerCase()) != -1 ||
            _.toLower(course.description).indexOf(searchQuery.value.toLowerCase()) != -1 ||
            _.toLower(course.level.label).indexOf(searchQuery.value.toLowerCase()) != -1 ||
            _.toLower(course.tagged.value).indexOf(searchQuery.value.toLowerCase()) != -1
          )
        }
      }
    })
  })

  const onLoadMore = () => {
    toggle.value = false
    loading.value = true
    // fetch data on load
    setTimeout(() => {
      courseList = _.take(filteredCourseData, inc(fetchAmount))
      loading.value = false
      // toggle loading on finish
      if (count.value >= courseData.total || levelQuery.value) {
        toggle.value = false
      } else {
        toggle.value = true
      }
    }, 700)
  }

  // Set meta data
  const { setMeta } = useMeta()

  // Set meta data on component creation
  setMeta({
    title: props.title,
    description: props.meta.description,
    og_title: props.title,
    og_description: props.meta.description,
  })

  watchEffect((value) => {
    // watch query request
    if (searchQuery.value < 1) {
      courseList = computedAsync(async () => {
        return await _.take(filteredCourseData, fetchAmount)
      }, null)
    }

    if (searchQuery.value || levelQuery.value) {
      courseList = computedAsync(async () => {
        return await filteredCourseData
      }, null)
    }
  })

  const form = useForm({
    email: '',
  })

  const subscribe = () => {
    loading.value = true
    form.post(route('subscribe.store'), {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })

    setTimeout(() => {
      loading.value = false
    }, 1000)
  }

  // Featured certifications computed property
  const featuredCertifications = computed(() => {
    if (!props.certifications || !props.certifications.data) return []

    return props.certifications.data
      .filter((certification) => certification.is_featured === true)
      .slice(0, 4) // Limit to 4 certifications
      .map((certification) => ({
        ...certification,
        // Ensure enrollments_count is available for components that need it
        enrollments_count:
          certification.enrollments_count ||
          (certification.enrollments ? certification.enrollments.length : 0),
      }))
  })

  // Add this helper function for certification navigation
  const navigateToCertification = (slug) => {
    router.visit(route('page.certification.show', slug))
  }

  // Featured courses computed property
  const featuredCourses = computed(() => {
    if (!courseData || !courseData.data) return []

    return courseData.data
      .filter((course) => course.is_featured === true || course.is_subscription === true)
      .slice(0, 4) // Limit to 4 courses
      .map((course) => ({
        ...course,
        // Ensure enrollments_count is available for components that need it
        enrollments_count:
          course.enrollments_count || (course.enrollments ? course.enrollments.length : 0),
      }))
  })

  // Helper functions for course cards
  const getPodCount = (course) => {
    const podCount = course.pods?.length || 0
    return `${podCount} ${podCount === 1 ? 'Pod' : 'Pods'}`
  }

  const getLessonCount = (course) => {
    if (!course.pods || !Array.isArray(course.pods)) {
      return '0 Lessons'
    }

    // Count total lessons across all pods using pod.lesson
    const totalLessons = course.pods.reduce((total, pod) => {
      const lessonCount = pod.lesson?.length || 0
      return total + lessonCount
    }, 0)

    return `${totalLessons} ${totalLessons === 1 ? 'Lesson' : 'Lessons'}`
  }

  const getFilteredTags = (course) => {
    if (!course.tagged || !Array.isArray(course.tagged)) {
      return []
    }

    return course.tagged.filter(
      (tag) => tag && (tag.tag_name || tag.name || (typeof tag === 'string' && tag.trim())),
    )
  }

  const truncateDescription = (description) => {
    if (!description) return ''

    // Remove HTML tags for length calculation
    const plainText = description.replace(/<[^>]*>/g, '')
    const maxLength = 300

    if (plainText.length <= maxLength) {
      return description
    }

    // Let CSS handle truncation with line-clamp for better display
    return description
  }

  const toggleSyllabus = (syllabusId) => {
    const syllabus = document.getElementById(syllabusId)
    if (syllabus) {
      syllabus.classList.toggle('_display:none')
    }
  }

  const navigateToCourse = (slug) => {
    router.visit(route('page.course.show', slug))
  }
</script>

<style scoped>
  /* ========================================
     ICON STYLES
     ======================================== */
  .email-error-icon {
    width: 24px;
    height: 24px;
    stroke-width: 0.2em;
  }

  .empty-icon {
    position: relative;
    width: 64px;
    height: 64px;
  }

  /* ========================================
     LEARNING PATHS / SPECIALIZATIONS
     ======================================== */
  .specializations-wrapper {
    max-width: 100%;
  }

  .learning-paths {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
  }

  @media (min-width: 768px) {
    .learning-paths {
      grid-template-columns: 1fr 1fr;
      gap: 2.5rem;
    }
  }

  .path-item {
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    margin-bottom: 0rem;
  }

  .path-content {
    padding: 1rem;
    border-left: 4px solid #e5e7eb;
    border-radius: 0.5rem;
    transition: all 0.3s ease;
  }

  /* Colored left borders for each specialization */
  .priority-path .path-content {
    border-left-color: #ff4242;
  }

  .finance-path .path-content {
    border-left-color: #009eda;
  }

  .access-path .path-content {
    border-left-color: #2fb079;
  }

  .renewable-path .path-content {
    border-left-color: #ff6b6b;
  }

  .compliance-path .path-content {
    border-left-color: #092e58;
  }

  .cleantech-path .path-content {
    border-left-color: #5f4995;
  }

  .risk-path .path-content {
    border-left-color: #dc2626;
  }

  .future-path .path-content {
    border-left-color: #1565c0;
  }

  .path-item:hover .path-content {
    background-color: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .path-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  .path-info {
    flex-grow: 1;
    text-align: left;
  }

  .path-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
    line-height: 1.3;
    text-align: left;
  }

  .path-description {
    font-size: 0.95rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
    text-align: left;
  }

  /* Future Path Special Styling */
  .future-path {
    opacity: 0.7;
  }

  .future-path:hover {
    opacity: 0.9;
  }

  /* ========================================
     RESPONSIVE - LEARNING PATHS
     ======================================== */
  @media (max-width: 767px) {
    .path-header {
      gap: 0.75rem;
    }

    .path-content {
      padding: 1.25rem;
      margin: 0.75rem 0;
    }

    .path-title {
      font-size: 1.125rem;
    }

    .path-description {
      font-size: 0.875rem;
    }

    .path-item:hover .path-content {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 480px) {
    .path-content {
      padding: 1rem;
      margin: 0.75rem 0;
    }
  }
</style>
